import {
  makeQuestionSetFormFields,
  type QuizFieldType,
  type QuizQuestion,
} from "@core/features/quiz";

/**
 * A question of a quiz shown to people who are not signed in, as it is drawn.
 *
 * The intake forms on the marketing site and the quiz an invitation asks
 * before signup are both quizzes answered by a stranger, and a question looks
 * and behaves the same on either. Each is drawn one of six ways the design
 * gives (a line of text, a paragraph, a web address, choose one, choose any
 * that apply, yes or no), or, for what the design does not draw, the way the
 * platform's own quizzes draw it: a statement somebody agrees to by going on
 * (a yes or no question set to `force_yes`), an upload, a slider.
 */
export type PublicQuizQuestionKind =
  | "text"
  | "textarea"
  | "url"
  | "select"
  | "multiselect"
  | "boolean"
  | "statement"
  | "other";

export type PublicQuizQuestion = {
  /** The key the answer is held under in the form: the quiz's own input key */
  name: string;
  label: string;
  help_text: string;
  kind: PublicQuizQuestionKind;
  is_required: boolean;
  /** The quiz form field this is drawn from */
  field: QuizFieldType;
  /** The quiz question itself, which an answer is packed against */
  question: QuizQuestion;
};

/** The kinds the design draws, as against those drawn as the platform's quizzes draw them. */
export const DESIGNED_KINDS: PublicQuizQuestionKind[] = [
  "text",
  "textarea",
  "url",
  "select",
  "multiselect",
  "boolean",
];

/** Whether a kind is answered by choosing rather than typing. */
export const CHOICE_KINDS: PublicQuizQuestionKind[] = ["select", "multiselect", "boolean"];

/** The most choices of one drawn as a row of pills; a longer list is a dropdown. */
const PILL_CHOICE_LIMIT = 6;

function kindOf(question: QuizQuestion): PublicQuizQuestionKind {
  switch (question.type) {
    case "yes/no":
      return question.config_object?.force_yes ? "statement" : "boolean";
    case "multiple choice":
      return question.validation_rules?.max_select === 1 ? "select" : "multiselect";
    case "longtext":
      return "textarea";
    case "url":
      return "url";
    case "text input":
      return "text";
    default:
      return "other";
  }
}

/** Turn a quiz's questions into the questions a public page draws. */
export function publicQuizQuestionsFrom(questions: QuizQuestion[] = []): PublicQuizQuestion[] {
  const fields = makeQuestionSetFormFields(questions);

  return questions.map((question, index) => {
    const field = fields[index];
    return {
      name: field.name,
      label: field.label,
      help_text: field.helpText ?? "",
      kind: kindOf(question),
      is_required: !!question.required,
      field,
      question,
    };
  });
}

/**
 * How a question of one of the designed kinds is drawn: the theme's form
 * field, and what it is given. Its options are the quiz question's choices,
 * answered by their ids.
 */
export function componentPropsFor(question: PublicQuizQuestion) {
  const options = question.field.options ?? [];

  // Each kind is drawn the way the design draws it: several answers as stacked
  // checkbox rows, a yes or no and a short list of choices as small pills, and
  // only a long list as a dropdown.
  switch (question.kind) {
    case "textarea":
      return { component: "Textarea" as const, placeholder: "Your message" };
    case "select":
      return options.length <= PILL_CHOICE_LIMIT
        ? { component: "SelectChips" as const, options, maxItems: 1 }
        : { component: "Select" as const, options, placeholder: `Select ${question.label.toLowerCase()}` };
    case "multiselect":
      return {
        component: "RadioGroup" as const,
        options,
        maxItems: options.length,
        direction: "vertical" as const,
      };
    case "boolean":
      return { component: "YesNoButtons" as const, options, variant: "chips" as const };
    case "url":
      return { component: "Input" as const, type: "url", placeholder: "https://" };
    default:
      return { component: "Input" as const };
  }
}
